import React, { FC, useMemo } from 'react';
import { Stack, Step, StepLabel, Stepper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from './FormController';
import { BasicEvent } from '../generated/api/dsv-public/model';
import { useIsMobile } from '../hooks/use-is-mobile';

type RegistrationFormHeaderProps = {
  event?: BasicEvent;
  activeForm: FormType;
  showBillingAddress?: boolean;
};

export const RegistrationFormHeader: FC<RegistrationFormHeaderProps> = ({
  activeForm,
  event,
  showBillingAddress = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const steps = showBillingAddress
    ? [
        t('forms.stepper.info'),
        t('forms.stepper.identification'),
        t('forms.stepper.registration-form'),
        t('forms.stepper.billing-info'),
        t('forms.stepper.confirmation'),
      ]
    : [
        t('forms.stepper.info'),
        t('forms.stepper.identification'),
        t('forms.stepper.registration-form'),
        t('forms.stepper.confirmation'),
      ];

  const activeStep: number = useMemo(() => {
    switch (activeForm) {
      case FormType.INITIAL_QUESTIONNAIRE:
        return 0;
      case FormType.REGISTRANT_INFO_FORM:
        return 1;
      case FormType.SINGLE_FORM:
      case FormType.MULTI_FORM:
        return 2;
      case FormType.BILLING_INFO_FORM:
        return 3;
      case FormType.SUBMISSION_FAILED:
        return showBillingAddress ? 4 : 3;
      case FormType.SUBMISSION_SUCCESSFUL:
        return showBillingAddress ? 5 : 4;
      default:
        return 0;
    }
  }, [activeForm, showBillingAddress]);

  const defaultStepperProps = {
    sx: { my: 2 },
  };

  const mobileStepperProps = {
    orientation: 'vertical',
    sx: { my: 2, mx: 'auto' },
  };

  const stepperProps = isMobile ? mobileStepperProps : defaultStepperProps;

  return (
    <Stack>
      <h1>{t('forms.common.title', { event })}</h1>
      <Stepper activeStep={activeStep} {...stepperProps}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Stack>
  );
};
